/**
 * FloorLineItemPricingPanel — Section 3 (iter-133).
 * Lightweight Sales tool that prices Floor Care, Floor Polishing, or
 * Floor Restoration for any commercial business type + sq. ft. using
 * the existing Commercial Quote Engine formulas. When a `property_id`
 * is provided, the engine pulls saved business_type + hard_floor_sqft
 * from the contract/property record so Sales doesn't re-key it.
 */
import { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Calculator } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  { key: 'floor_care', label: 'Floor Care (recurring maintenance)' },
  { key: 'floor_polishing', label: 'Floor Polishing' },
  { key: 'floor_restoration', label: 'Floor Restoration' },
];

// Matches the 9 general commercial business types in the engine profiles.
const BUSINESS_TYPES = ['office', 'retail', 'medical', 'restaurant', 'hospitality', 'education', 'industrial', 'warehouse', 'general_commercial'];
const FLOOR_TYPES = ['vinyl', 'tile', 'polished_concrete', 'terrazzo', 'carpet'];

export default function FloorLineItemPricingPanel({ testId = 'floor-line-item-panel' }) {
  const [form, setForm] = useState({ service_key: 'floor_care', property_id: '', business_type: 'office', hard_floor_sqft: '', floor_type: 'vinyl' });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    const payload = {
      service_key: form.service_key,
      property_id: form.property_id || undefined,
      business_type: form.business_type,
      hard_floor_sqft: Number(form.hard_floor_sqft) || undefined,
      floor_type: form.floor_type,
    };
    const r = await fetch(`${API}/api/commercial-quote/floor-line-item`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    setBusy(false);
    if (r.ok) setResult(await r.json());
    else {
      const j = await r.json().catch(() => ({}));
      toast.error(j.detail || 'Failed to price');
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 mt-4" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(201,169,98,0.15)', border: '1px solid rgba(201,169,98,0.4)' }}>
          <Calculator className="h-5 w-5" style={{ color: '#C9A962' }} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Floor Services · Line-Item Pricing
          </h3>
          <p className="text-[11px] text-zinc-500">
            Pulls business type + sq. ft. from a property when a property_id is supplied.
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="grid sm:grid-cols-6 gap-3 items-end">
        <div className="sm:col-span-2">
          <Label className="text-xs text-zinc-400">Service</Label>
          <Select value={form.service_key} onValueChange={(v) => setForm((f) => ({ ...f, service_key: v }))}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="fli-service"><SelectValue /></SelectTrigger>
            <SelectContent>{SERVICES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Property ID (optional)</Label>
          <Input value={form.property_id} onChange={(e) => setForm((f) => ({ ...f, property_id: e.target.value }))}
                 placeholder="prop_..." className="bg-zinc-800 border-zinc-700 text-white" data-testid="fli-property" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Business type</Label>
          <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="fli-biz"><SelectValue /></SelectTrigger>
            <SelectContent>{BUSINESS_TYPES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Hard-floor sq ft</Label>
          <Input type="number" value={form.hard_floor_sqft} onChange={(e) => setForm((f) => ({ ...f, hard_floor_sqft: e.target.value }))}
                 className="bg-zinc-800 border-zinc-700 text-white" data-testid="fli-sqft" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Floor type</Label>
          <Select value={form.floor_type} onValueChange={(v) => setForm((f) => ({ ...f, floor_type: v }))}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="fli-floor-type"><SelectValue /></SelectTrigger>
            <SelectContent>{FLOOR_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-6 flex items-center gap-3">
          <Button type="submit" disabled={busy} className="bg-[#C9A962] text-black hover:bg-[#B8975A] font-bold" data-testid="fli-submit">
            {busy ? 'Pricing…' : 'Price this line item'}
          </Button>
          {result && (
            <div className="text-sm text-zinc-200 flex-1 flex flex-wrap gap-4" data-testid="fli-result">
              {result.one_time && (
                <span><span className="text-zinc-500">One-time:</span> <strong className="text-[#C9A962]">${result.one_time.low.toLocaleString()} – ${result.one_time.high.toLocaleString()}</strong></span>
              )}
              <span><span className="text-zinc-500">Recurring / mo:</span> <strong className="text-[#C9A962]">${result.recurring_monthly.toLocaleString()}</strong></span>
              <span className="text-[11px] text-zinc-500">({result.business_type} · {result.hard_floor_sqft.toLocaleString()} sq ft · {result.floor_type})</span>
            </div>
          )}
        </div>
      </form>
    </Card>
  );
}
